/**
 * Property Gallery Module
 *
 * Handles:
 * - Thumbnail clicks to swap hero image
 * - Bento gallery with images sorted by category
 * - Single image lightbox within bento gallery
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';

export default class PropertyGallery {
  constructor() {
    this.gallery = document.querySelector('[data-property-gallery]');

    if (!this.gallery) return;

    // Hero showcase elements (new filmstrip approach)
    this.heroShowcase = this.gallery.querySelector('[data-hero-showcase]');
    this.heroImages = this.gallery.querySelectorAll('[data-hero-image]');
    this.filmstripItems = this.gallery.querySelectorAll('[data-filmstrip-item]');

    // Legacy hero image elements (fallback)
    this.heroImage = this.gallery.querySelector('[data-main-image]');
    this.thumbButtons = this.gallery.querySelectorAll('[data-thumb-swap]');

    // Bento gallery elements
    this.bentoOpenBtns = document.querySelectorAll('[data-bento-open]');
    this.bentoGallery = document.querySelector('[data-bento-gallery]');
    this.bentoContent = this.bentoGallery?.querySelector('[data-bento-content]');
    this.bentoCloseBtn = this.bentoGallery?.querySelector('[data-bento-close]');

    // Bento lightbox elements
    this.bentoLightbox = document.querySelector('[data-bento-lightbox]');
    this.bentoLightboxImage = this.bentoLightbox?.querySelector('[data-bento-lightbox-image]');
    this.bentoLightboxClose = this.bentoLightbox?.querySelector('[data-bento-lightbox-close]');
    this.bentoLightboxPrev = this.bentoLightbox?.querySelector('[data-bento-lightbox-prev]');
    this.bentoLightboxNext = this.bentoLightbox?.querySelector('[data-bento-lightbox-next]');
    this.bentoLightboxCategory = this.bentoLightbox?.querySelector('[data-bento-lightbox-category]');
    this.bentoLightboxCurrent = this.bentoLightbox?.querySelector('[data-bento-lightbox-current]');
    this.bentoLightboxTotal = this.bentoLightbox?.querySelector('[data-bento-lightbox-total]');

    // Load images data from JSON
    const galleryData = this.loadImagesData();
    this.images = galleryData.images || [];
    this.groups = galleryData.groups || [];
    this.useGroups = galleryData.useGroups || false;
    this.currentImageIndex = 0;
    this.activeThumbIndex = 0;
    this.activeHeroIndex = 0;

    // Category display names (fallback for auto-detection mode)
    this.categoryLabels = {
      exterior: 'Exterior & Views',
      living: 'Living Areas',
      kitchen: 'Kitchen & Dining',
      bedroom: 'Bedrooms',
      bathroom: 'Bathrooms',
    };

    this.init();
  }

  init() {
    if (!this.images.length) return;

    this.bindEvents();
    this.generateBentoContent();

    // Update lightbox total count
    if (this.bentoLightboxTotal) {
      this.bentoLightboxTotal.textContent = this.images.length;
    }
  }

  /**
   * Load images data from embedded JSON
   */
  loadImagesData() {
    const jsonScript = this.gallery.querySelector('[data-gallery-images]');
    if (!jsonScript) return { images: [], groups: [], useGroups: false };

    try {
      const data = JSON.parse(jsonScript.textContent);
      // Handle both old format (array) and new format (object with images/groups)
      if (Array.isArray(data)) {
        return { images: data, groups: [], useGroups: false };
      }
      return data;
    } catch (e) {
      console.error('Failed to parse gallery images data:', e);
      return { images: [], groups: [], useGroups: false };
    }
  }

  bindEvents() {
    // Film strip clicks - swap hero showcase image
    this.filmstripItems.forEach((btn) => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.filmstripItem, 10);
        this.swapHeroShowcase(index);
      });
    });

    // Legacy thumbnail clicks - swap hero image (fallback)
    this.thumbButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.thumbSwap, 10);
        const largeSrc = btn.dataset.largeSrc;
        const alt = btn.dataset.alt;
        this.swapHeroImage(largeSrc, alt, index);
      });
    });

    // Open bento gallery
    this.bentoOpenBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.openBentoGallery());
    });

    // Close bento gallery
    this.bentoCloseBtn?.addEventListener('click', () => this.closeBentoGallery());

    // Close bento lightbox
    this.bentoLightboxClose?.addEventListener('click', () => this.closeBentoLightbox());

    // Lightbox navigation
    this.bentoLightboxPrev?.addEventListener('click', () => this.prevImage());
    this.bentoLightboxNext?.addEventListener('click', () => this.nextImage());

    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Touch/swipe support
    this.initSwipe();
  }

  /**
   * Swap hero showcase image (new filmstrip approach)
   * Uses CSS transitions by toggling is-active class
   */
  swapHeroShowcase(index) {
    if (this.activeHeroIndex === index) return;

    // Update active hero image
    this.heroImages.forEach((img) => {
      const imgIndex = parseInt(img.dataset.heroImage, 10);
      img.classList.toggle('is-active', imgIndex === index);
    });

    // Update active filmstrip item
    this.filmstripItems.forEach((btn) => {
      const btnIndex = parseInt(btn.dataset.filmstripItem, 10);
      btn.classList.toggle('is-active', btnIndex === index);
    });

    this.activeHeroIndex = index;
  }

  /**
   * Swap the main hero image with smooth transition (legacy)
   */
  swapHeroImage(src, alt, index) {
    if (!this.heroImage || this.activeThumbIndex === index) return;

    // Update active thumb
    this.thumbButtons.forEach((btn, i) => {
      btn.classList.toggle('is-active', parseInt(btn.dataset.thumbSwap, 10) === index);
    });
    this.activeThumbIndex = index;

    // Animate out
    gsap.to(this.heroImage, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: () => {
        // Swap image
        this.heroImage.src = src;
        this.heroImage.alt = alt;

        // Animate in
        gsap.to(this.heroImage, {
          opacity: 1,
          duration: 0.4,
          ease: 'power2.out',
        });
      },
    });
  }

  /**
   * Generate bento gallery content organized by category/group
   */
  generateBentoContent() {
    if (!this.bentoContent) return;

    let html = '';

    if (this.useGroups && this.groups.length > 0) {
      // Use custom groups from backend
      this.groups.forEach((group) => {
        const groupImages = this.images.filter((img) => img.category === group.id);
        if (groupImages.length === 0) return;

        html += this.generateGroupSection(group.id, group.name, groupImages);
      });
    } else {
      // Fallback: Group images by category (auto-detection)
      const grouped = this.images.reduce((acc, img) => {
        const cat = img.category || 'exterior';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(img);
        return acc;
      }, {});

      // Order of categories
      const categoryOrder = ['exterior', 'living', 'kitchen', 'bedroom', 'bathroom'];

      categoryOrder.forEach((cat) => {
        const images = grouped[cat];
        if (!images || images.length === 0) return;

        const label = this.categoryLabels[cat] || cat;
        html += this.generateGroupSection(cat, label, images);
      });
    }

    this.bentoContent.innerHTML = html;

    // Bind click events to bento items
    const bentoItems = this.bentoContent.querySelectorAll('[data-bento-item]');
    bentoItems.forEach((item) => {
      item.addEventListener('click', () => {
        const index = parseInt(item.dataset.bentoItem, 10);
        this.openBentoLightbox(index);
      });
    });
  }

  /**
   * Generate HTML for a gallery section
   */
  generateGroupSection(groupId, groupName, images) {
    return `
      <section class="bento-gallery__section" data-category="${groupId}">
        <div class="bento-gallery__section-header">
          <h3 class="bento-gallery__section-title">${groupName}</h3>
          <div class="bento-gallery__section-line"></div>
          <span class="bento-gallery__section-count">${images.length} ${images.length === 1 ? 'photo' : 'photos'}</span>
        </div>
        <div class="bento-gallery__grid">
          ${images
            .map((img, i) => {
              // Determine variation class for bento effect
              let variationClass = '';
              if (i === 0 && images.length >= 3) {
                variationClass = 'bento-gallery__item--featured';
              } else if (i === 2 && images.length >= 4) {
                variationClass = 'bento-gallery__item--tall';
              }

              return `
              <button
                class="bento-gallery__item ${variationClass}"
                data-bento-item="${img.index}"
                data-category="${groupId}"
                data-category-name="${img.categoryName || groupName}"
                aria-label="View ${img.alt}"
              >
                <img
                  src="${img.src}"
                  alt="${img.alt}"
                  loading="lazy"
                >
                <span class="bento-gallery__label">${img.alt}</span>
              </button>
            `;
            })
            .join('')}
        </div>
      </section>
    `;
  }

  /**
   * Open the bento gallery
   */
  openBentoGallery() {
    if (!this.bentoGallery) return;

    this.bentoGallery.classList.add('is-open');
    this.bentoGallery.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';

    // Stop Lenis smooth scroll
    this.stopLenis();

    // Animate in
    gsap.fromTo(
      this.bentoGallery,
      { opacity: 0 },
      { opacity: 1, duration: 0.4, ease: 'power2.out' }
    );

    // Stagger animate the items
    const items = this.bentoGallery.querySelectorAll('.bento-gallery__item');
    gsap.fromTo(
      items,
      { opacity: 0, y: 20, scale: 0.95 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        stagger: 0.03,
        ease: 'power2.out',
        delay: 0.2,
      }
    );
  }

  /**
   * Close the bento gallery
   */
  closeBentoGallery() {
    if (!this.bentoGallery) return;

    gsap.to(this.bentoGallery, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in',
      onComplete: () => {
        this.bentoGallery.classList.remove('is-open');
        this.bentoGallery.setAttribute('hidden', '');
        document.body.style.overflow = '';

        // Restart Lenis smooth scroll
        this.startLenis();
      },
    });
  }

  /**
   * Get display name for an image's category/group
   */
  getCategoryDisplayName(image) {
    // First check if image has a custom categoryName (from backend groups)
    if (image.categoryName) {
      return image.categoryName;
    }
    // Then check if we have groups and can find the matching one
    if (this.useGroups && this.groups.length > 0) {
      const group = this.groups.find((g) => g.id === image.category);
      if (group) {
        return group.name;
      }
    }
    // Fallback to predefined labels
    return this.categoryLabels[image.category] || image.category;
  }

  /**
   * Open the bento lightbox with a specific image
   */
  openBentoLightbox(index) {
    if (!this.bentoLightbox || !this.bentoLightboxImage) return;

    this.currentImageIndex = index;
    const image = this.images[index];

    if (!image) return;

    // Set image
    this.bentoLightboxImage.src = image.src;
    this.bentoLightboxImage.alt = image.alt;

    // Update category and counter
    if (this.bentoLightboxCategory) {
      this.bentoLightboxCategory.textContent = this.getCategoryDisplayName(image);
    }
    if (this.bentoLightboxCurrent) {
      this.bentoLightboxCurrent.textContent = index + 1;
    }

    // Show lightbox
    this.bentoLightbox.classList.add('is-open');
    this.bentoLightbox.removeAttribute('hidden');

    // Animate in
    gsap.fromTo(
      this.bentoLightbox,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: 'power2.out' }
    );

    gsap.fromTo(
      this.bentoLightboxImage,
      { scale: 0.9, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.4, ease: 'power2.out', delay: 0.1 }
    );
  }

  /**
   * Close the bento lightbox (back to bento gallery)
   */
  closeBentoLightbox() {
    if (!this.bentoLightbox) return;

    gsap.to(this.bentoLightbox, {
      opacity: 0,
      duration: 0.25,
      ease: 'power2.in',
      onComplete: () => {
        this.bentoLightbox.classList.remove('is-open');
        this.bentoLightbox.setAttribute('hidden', '');
      },
    });
  }

  /**
   * Navigate to next image in lightbox
   */
  nextImage() {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
    this.updateBentoLightboxImage();
  }

  /**
   * Navigate to previous image in lightbox
   */
  prevImage() {
    this.currentImageIndex = (this.currentImageIndex - 1 + this.images.length) % this.images.length;
    this.updateBentoLightboxImage();
  }

  /**
   * Update the lightbox image with animation
   */
  updateBentoLightboxImage() {
    const image = this.images[this.currentImageIndex];
    if (!image || !this.bentoLightboxImage) return;

    gsap.to(this.bentoLightboxImage, {
      opacity: 0,
      scale: 0.95,
      duration: 0.2,
      ease: 'power2.in',
      onComplete: () => {
        this.bentoLightboxImage.src = image.src;
        this.bentoLightboxImage.alt = image.alt;

        gsap.to(this.bentoLightboxImage, {
          opacity: 1,
          scale: 1,
          duration: 0.2,
          ease: 'power2.out',
        });
      },
    });

    // Update category and counter
    if (this.bentoLightboxCategory) {
      this.bentoLightboxCategory.textContent = this.getCategoryDisplayName(image);
    }
    if (this.bentoLightboxCurrent) {
      this.bentoLightboxCurrent.textContent = this.currentImageIndex + 1;
    }
  }

  /**
   * Handle keyboard navigation
   */
  handleKeyboard(e) {
    // Bento lightbox is open
    if (this.bentoLightbox?.classList.contains('is-open')) {
      switch (e.key) {
        case 'Escape':
          this.closeBentoLightbox();
          break;
        case 'ArrowLeft':
          this.prevImage();
          break;
        case 'ArrowRight':
          this.nextImage();
          break;
      }
      return;
    }

    // Bento gallery is open
    if (this.bentoGallery?.classList.contains('is-open')) {
      if (e.key === 'Escape') {
        this.closeBentoGallery();
      }
    }
  }

  /**
   * Initialize touch/swipe support
   */
  initSwipe() {
    if (!this.bentoLightbox) return;

    let startX = 0;
    let endX = 0;
    const minSwipeDistance = 50;

    this.bentoLightbox.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
      },
      { passive: true }
    );

    this.bentoLightbox.addEventListener(
      'touchend',
      (e) => {
        endX = e.changedTouches[0].clientX;
        const distance = startX - endX;

        if (Math.abs(distance) > minSwipeDistance) {
          if (distance > 0) {
            this.nextImage();
          } else {
            this.prevImage();
          }
        }
      },
      { passive: true }
    );
  }

  /**
   * Stop Lenis smooth scroll
   */
  stopLenis() {
    const lenis = window.TRE?.getLenis?.();
    if (lenis) {
      lenis.stop();
    }
  }

  /**
   * Start Lenis smooth scroll
   */
  startLenis() {
    const lenis = window.TRE?.getLenis?.();
    if (lenis) {
      lenis.start();
    }
  }

  destroy() {
    // Cleanup if needed
  }
}
